import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EnglishCourse } from 'src/app/models/english-course';
import { ActivatedRoute } from '@angular/router';
import {
  DomSanitizer,
  SafeUrl
} from '@angular/platform-browser';

import { CourseService } from 'src/app/services/course.service';
import {
  getLocalTranslate
} from 'src/app/helpers/global-functions';

import { Question } from 'src/app/models/question.model';
import { SeoService } from 'src/app/services/seo.service';

@Component({
  selector: 'app-english-course',
  templateUrl: './english-course.component.html',
  styleUrls: ['./english-course.component.css']
})
export class EnglishCourseComponent implements OnInit {

  course: EnglishCourse =
    new EnglishCourse();

  relatedCourses: EnglishCourse[] = [];

  cloneRelatedCourses: EnglishCourse[] = [];

  imageUrl: SafeUrl;

  public htmlContent: string;

  home: string = 'Home';

  englishLesson: string = 'Lessons';

  courseDetail: string = 'Course Detail';

  relateCourseText: string = 'Related Courses';

  showQuizDialog: boolean = false;

  showScoreResult: boolean = false;

  showHistoryButton: boolean = false;

  resultScore: number = 0;

  cloneQuestions: Question[] = [];

  constructor(
    private route: ActivatedRoute,
    private firestore: AngularFirestore,
    private sanizer: DomSanitizer,
    private courseService: CourseService,
    private seoService: SeoService
  ) {}

  ngOnInit(): void {

    this.imageUrl =
      this.sanizer
        .bypassSecurityTrustResourceUrl('');

    this.getCourse();

    this.courseService.updateViewCount(
      this.route.snapshot.params['id']
    );

    this.needTranslate();
  }

  needTranslate() {

    this.home =
      getLocalTranslate(
        'home',
        this.home
      );

    this.englishLesson =
      getLocalTranslate(
        'english-lesson',
        this.englishLesson
      );

    this.courseDetail =
      getLocalTranslate(
        'course-detail',
        this.courseDetail
      );

    this.relateCourseText =
      getLocalTranslate(
        'related-courses',
        this.relateCourseText
      );
  }

  getCourse() {

    const id =
      this.route.snapshot.params['id'];

    const id$ =
      new Subject<string>();

    const queryObservable =
      id$.pipe(
        switchMap(id =>
          this.firestore
            .collection('english-courses')
            .doc(id)
            .get()
        )
      );

    queryObservable.subscribe(
      queriedItem => {

        if (!queriedItem.exists) {
          return;
        }

        const course: EnglishCourse =
          new EnglishCourse();

        course.id =
          queriedItem.id;

        Object.assign(
          course,
          queriedItem.data()
        );

        this.course =
          course;

        // ==========================================
        // Quiz
        // ==========================================

        if (course.quiz) {

          this.cloneQuestions =
            [...course.quiz];
        }

        // ==========================================
        // Description
        // ==========================================

        this.htmlContent =
          this.course.description;

        // ==========================================
        // Course Image
        // ==========================================

        const image =
          this.course.image &&
          this.course.image.fileUrl
            ? this.course.image.fileUrl
            : 'https://camedu-learning.com/assets/images/default-course.jpg';

        this.imageUrl =
          this.sanizer
            .bypassSecurityTrustResourceUrl(image);

        // ==========================================
        // SEO
        // ==========================================

        const courseName =
          this.course.nameEn ||
          'English Lesson';

        const description =
          this.course.descriptionEn ||
          this.course.description ||
          'Learn English with CamEdu.';

        const pageUrl =
          'https://camedu-learning.com/english-course/' +
          this.course.id;

        this.seoService.setCourseSeo(
          courseName + ' | CamEdu',
          description,
          image,
          pageUrl
        );

        // ==========================================

        this.getRelatedCourses();
      }
    );

    id$.next(id);
  }

  getRelatedCourses() {

    const levelId =
      this.course.levelId;

    const levelId$ =
      new Subject<string>();

    const queryObservable =
      levelId$.pipe(
        switchMap(levelId =>
          this.firestore.collection(
            'english-courses',
            ref => {

              let query: any =
                ref.where(
                  'status',
                  '==',
                  1
                );

              // Level filter
              query =
                query.where(
                  'levelId',
                  '==',
                  levelId
                );

              // Order by course name
              query =
                query.orderBy(
                  'nameEn'
                );

              // Get 11 courses
              query =
                query.limit(11);

              return query;
            }
          ).get()
        )
      );

    queryObservable.subscribe(
      queriedItems => {

        let courses =
          queriedItems.docs.map(
            doc => {

              const course:
                EnglishCourse =
                new EnglishCourse();

              course.id =
                doc.id;

              Object.assign(
                course,
                doc.data()
              );

              return course;
            }
          );

        // Keep original result
        this.cloneRelatedCourses =
          courses;

        // Remove current course
        courses =
          courses.filter(
            course =>
              course.id !==
              this.course.id
          );

        // Maximum 10 related courses
        this.relatedCourses =
          courses.slice(0, 10);
      }
    );

    levelId$.next(levelId);
  }

  onChooseCourse(course: EnglishCourse) {

    if (!course) {
      return;
    }

    // ==========================================
    // Quiz
    // ==========================================

    if (course.quiz) {

      this.cloneQuestions =
        [...course.quiz];
    }

    // ==========================================
    // Set Course
    // ==========================================

    this.course =
      course;

    this.htmlContent =
      this.course.descriptionEn ||
      this.course.description;

    // ==========================================
    // Image
    // ==========================================

    const image =
      this.course.image &&
      this.course.image.fileUrl
        ? this.course.image.fileUrl
        : 'https://camedu-learning.com/assets/images/default-course.jpg';

    this.imageUrl =
      this.sanizer
        .bypassSecurityTrustResourceUrl(image);

    // ==========================================
    // SEO
    // ==========================================

    const courseName =
      this.course.nameEn ||
      'English Lesson';

    const description =
      this.course.descriptionEn ||
      this.course.description ||
      'Learn English with CamEdu.';

    const pageUrl =
      'https://camedu-learning.com/english-course/' +
      this.course.id;

    this.seoService.setCourseSeo(
      courseName + ' | CamEdu',
      description,
      image,
      pageUrl
    );

    // ==========================================
    // Related Courses
    // ==========================================

    let courses =
      this.cloneRelatedCourses;

    courses =
      courses.filter(
        course =>
          course.id !==
          this.course.id
      );

    this.relatedCourses =
      courses.slice(0, 10);
  }

  onShowQuiz() {

    this.showQuizDialog =
      true;
  }

  onDismissDialog() {

    this.showQuizDialog =
      false;

    this.showScoreResult =
      false;

    this.clearAnswer();
  }

  onSubmitQuiz() {

    let score = 0;

    this.course.quiz.forEach(q => {

      const isCorrect =
        q.correctAnswerId ===
        q.studentAnswer;

      if (isCorrect) {
        score += q.score;
      }
    });

    this.resultScore =
      score;

    this.showScoreResult =
      true;
  }

  clearAnswer() {

    this.course.quiz.map(q => {

      q.studentAnswer = '';

      return q;
    });
  }

  onRetryQuiz() {

    this.clearAnswer();

    this.course.quiz =
      [...this.cloneQuestions];

    this.showScoreResult =
      false;
  }

  onShuffleQuiz() {

    this.clearAnswer();

    this.shuffleArray(
      this.course.quiz
    );

    this.showScoreResult =
      false;
  }

  shuffleArray<T>(
    array: T[]
  ): T[] {

    let currentIndex =
      array.length;

    let randomIndex: number;

    while (
      currentIndex !== 0
    ) {

      randomIndex =
        Math.floor(
          Math.random() *
          currentIndex
        );

      currentIndex--;

      [
        array[currentIndex],
        array[randomIndex]
      ] = [
        array[randomIndex],
        array[currentIndex]
      ];
    }

    return array;
  }
}